/**
 * Outbound Access (MCP) — Tenant Admin Page
 *
 * Allows tenant admins to manage MCP server configurations.
 * Workspaces is the first applet surface that supports MCP.
 * Applet API operations are declared by enabled applet manifests.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ServerStackIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ArrowRightIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Spinner,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';

interface MCPConfig {
  id: number;
  customer_id: string;
  server_type: string;
  server_name: string;
  display_name: string | null;
  description: string | null;
  icon_url: string | null;
  is_enabled: boolean;
  allowed_workspace_ids: number[] | null;
  allowed_tool_names: string[] | null;
  allowed_applet_api_operations: AppletApiAllowlist | null;
  enabled_widgets: string[] | null;
  rate_limit_rpm: number;
  rate_limit_rph: number;
  max_concurrent_sessions: number;
  max_async_operations: number;
  oauth_client_id: string | null;
  oauth_redirect_uris: string[];
  server_url: string;
  authorization_url: string;
  token_url: string;
  oauth_token_endpoint_auth_method: string;
}

type AppletApiAllowlist = Record<string, string[]>;

interface Workspace {
  id: number;
  name: string;
  status: string;
}

const APPLET_API_PARENT_TOOL = 'list_applet_api_operations';

const KNOWN_TOOLS = [
  'list_workspaces',
  'select_workspace',
  'list_knowledge_bases',
  'search_documents',
  'chat',
  'ask_question',
  'list_data_connections',
  'get_document',
  'upload_document',
  'trigger_sync',
  'suggest_correction',
  'structured_query',
  APPLET_API_PARENT_TOOL,
] as const;

const TOOL_LABELS: Record<string, string> = {
  [APPLET_API_PARENT_TOOL]: 'Applet APIs',
};

const PRD_MCP_TOOLS = ['start_prd_session', 'continue_prd_session', 'generate_prd'] as const;

type McpAppletTab = 'workspaces' | 'applet_apis' | 'prd_generator';

const isToolAllowedByConfig = (config: MCPConfig | null, toolName: string): boolean => {
  if (!config) return false;
  const allowedToolNames = config.allowed_tool_names ?? [];
  return allowedToolNames.length === 0 || allowedToolNames.includes(toolName);
};

const toolDisplayLabel = (tool: string) => TOOL_LABELS[tool] ?? tool;

const hasSelectedAppletApiOperations = (allowlist: AppletApiAllowlist): boolean =>
  Object.values(allowlist).some((operations) => operations.length > 0);

const reconcileAppletApiParentTool = (allowedToolNames: string[], allowlist: AppletApiAllowlist): string[] => {
  const hasAppletApis = hasSelectedAppletApiOperations(allowlist);
  if (!hasAppletApis) {
    return allowedToolNames.filter((tool) => tool !== APPLET_API_PARENT_TOOL);
  }
  if (allowedToolNames.length > 0 && !allowedToolNames.includes(APPLET_API_PARENT_TOOL)) {
    return [...allowedToolNames, APPLET_API_PARENT_TOOL];
  }
  return allowedToolNames;
};

interface MCPAppletAPIOperation {
  operation_id: string;
  tool_name: string;
  title: string;
  method: string;
  path: string;
  required_permission: string;
  read_only: boolean;
  description: string | null;
}

interface MCPAppletAPI {
  applet_id: string;
  display_name: string;
  description: string | null;
  operations: MCPAppletAPIOperation[];
}

interface MCPTenantPolicy {
  tenant_enabled: boolean;
  default_deny: boolean;
  policy_state: 'enabled' | 'disabled' | 'not_configured';
  policy_reason: string;
  available_tools: Array<{
    name: string;
    title: string;
    required_permission: string;
    requires_active_context_pack: boolean;
  }>;
  available_applet_apis?: MCPAppletAPI[];
  server_url: string;
  authorization_url: string;
  token_url: string;
  oauth_authorization_server_metadata_url: string;
  oauth_protected_resource_metadata_url: string;
  oauth_token_endpoint_auth_method: string;
}

const parseRedirectUris = (value: string): string[] =>
  value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const getOriginFromUrl = (value?: string): string | null => {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const getRuntimeOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return '';
};

const getPrdMcpConnectionDetails = (policy: MCPTenantPolicy | null) => {
  const origin = getOriginFromUrl(policy?.server_url) || getRuntimeOrigin();
  return {
    serverUrl: `${origin}/mcp/prd`,
    protectedResourceMetadataUrl: `${origin}/.well-known/oauth-protected-resource/mcp/prd`,
    authorizationMetadataUrl: `${origin}/.well-known/oauth-authorization-server/mcp/prd/oauth`,
    authorizationUrl: `${origin}/mcp/prd/oauth/authorize`,
    tokenUrl: `${origin}/mcp/prd/oauth/token`,
  };
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown; message?: unknown } } }).response;
    const detail = response?.data?.detail ?? response?.data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (response?.status === 403) return 'You do not have permission to change MCP settings for this tenant.';
    if (response?.status === 404) return 'MCP server configuration was not found for this tenant.';
    if (response?.status) return `${fallback} (HTTP ${response.status}).`;
  }
  return fallback;
};

export default function McpServersPage() {
  const { push: addToast } = useToasts();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [configs, setConfigs] = useState<MCPConfig[]>([]);
  const [policy, setPolicy] = useState<MCPTenantPolicy | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeAppletTab, setActiveAppletTab] = useState<McpAppletTab>('workspaces');
  const [accessHelpOpen, setAccessHelpOpen] = useState(false);

  // Draft state for editing the selected applet MCP server.
  const [draft, setDraft] = useState<{
    display_name: string;
    description: string;
    is_enabled: boolean;
    allowed_workspace_ids: number[];
    allowed_tool_names: string[];
    allowed_applet_api_operations: AppletApiAllowlist;
    rate_limit_rpm: number;
    rate_limit_rph: number;
    oauth_client_id: string;
    oauth_redirect_uris_text: string;
  }>({
    display_name: '',
    description: '',
    is_enabled: false,
    allowed_workspace_ids: [],
    allowed_tool_names: [],
    allowed_applet_api_operations: {},
    rate_limit_rpm: 30,
    rate_limit_rph: 500,
    oauth_client_id: '',
    oauth_redirect_uris_text: '',
  });
  const fetchConfigs = useCallback(async () => {
    const { data } = await AXIOS_INSTANCE.get<MCPConfig[]>('/v1/mcp/admin/configs');
    setConfigs(data);
  }, []);

  const fetchPolicy = useCallback(async () => {
    const { data } = await AXIOS_INSTANCE.get<MCPTenantPolicy>('/v1/mcp/admin/policy');
    setPolicy(data);
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const { data } = await AXIOS_INSTANCE.get<{ domains?: Workspace[] } | Workspace[]>('/v1/ragflow/domains');
      setWorkspaces(Array.isArray(data) ? data : data.domains ?? []);
    } catch {
      /* Workspaces are optional; the MCP policy remains visible. */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([fetchConfigs(), fetchPolicy(), fetchWorkspaces()]).then((results) => {
      if (cancelled) return;
      const failed = results.slice(0, 2).find((result) => result.status === 'rejected');
      if (failed?.status === 'rejected') {
        setLoadError(getApiErrorMessage(failed.reason, 'Could not load MCP settings.'));
      } else {
        setLoadError(null);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [fetchConfigs, fetchPolicy, fetchWorkspaces]);

  const workspaceMap = useMemo(() => new Map(workspaces.map((w) => [w.id, w.name])), [workspaces]);
  const workspaceConfigs = useMemo(() => configs.filter((config) => config.server_name === 'workspaces'), [configs]);
  const workspaceConfig = workspaceConfigs[0] ?? null;
  const appletApiDescriptors = useMemo(() => policy?.available_applet_apis ?? [], [policy]);
  const appletSectionRef = useRef<HTMLDivElement | null>(null);

  const copyValue = useCallback(
    async (label: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        addToast({ kind: 'success', message: `${label} copied` });
      } catch {
        addToast({
          kind: 'error',
          message: `Failed to copy ${label.toLowerCase()}`,
        });
      }
    },
    [addToast],
  );

  // Use the shared API client so token refresh and tenant context match the rest of the app.
  const handleToggle = async (config: MCPConfig) => {
    try {
      const { data: updated } = await AXIOS_INSTANCE.post<MCPConfig>(`/v1/mcp/admin/configs/${config.id}/toggle`);
      setConfigs((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      await fetchPolicy();
      addToast({ kind: 'success', message: `MCP server ${updated.is_enabled ? 'enabled' : 'disabled'}` });
    } catch (error) {
      addToast({ kind: 'error', message: getApiErrorMessage(error, 'Failed to toggle MCP server') });
    }
  };

  const startEdit = (config: MCPConfig) => {
    setEditingId(config.id);
    setDraft({
      display_name: config.display_name ?? '',
      description: config.description ?? '',
      is_enabled: config.is_enabled,
      allowed_workspace_ids: config.allowed_workspace_ids ?? [],
      allowed_tool_names: config.allowed_tool_names ?? [],
      allowed_applet_api_operations: config.allowed_applet_api_operations ?? {},
      rate_limit_rpm: config.rate_limit_rpm,
      rate_limit_rph: config.rate_limit_rph,
      oauth_client_id: config.oauth_client_id ?? '',
      oauth_redirect_uris_text: (config.oauth_redirect_uris ?? []).join('\n'),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const scrollToAppletSection = useCallback(() => {
    const scroll = () => {
      appletSectionRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scroll);
    } else {
      scroll();
    }
  }, []);

  const openApplet = useCallback(
    (tab: McpAppletTab) => {
      setActiveAppletTab(tab);
      scrollToAppletSection();
    },
    [scrollToAppletSection],
  );

  const editWorkspaceApplet = useCallback(() => {
    if (workspaceConfig) {
      startEdit(workspaceConfig);
    }
    openApplet('workspaces');
  }, [openApplet, workspaceConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      if (editingId) {
        const { data: updated } = await AXIOS_INSTANCE.patch<MCPConfig>(
          `/v1/mcp/admin/configs/${editingId}`,
          {
            display_name: draft.display_name || null,
            description: draft.description || null,
            is_enabled: draft.is_enabled,
            allowed_workspace_ids: draft.allowed_workspace_ids,
            allowed_tool_names: draft.allowed_tool_names.length ? draft.allowed_tool_names : [],
            allowed_applet_api_operations: draft.allowed_applet_api_operations,
            rate_limit_rpm: draft.rate_limit_rpm,
            rate_limit_rph: draft.rate_limit_rph,
            oauth_client_id: draft.oauth_client_id.trim() || null,
            oauth_redirect_uris: parseRedirectUris(draft.oauth_redirect_uris_text),
          },
        );
        setConfigs((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        setEditingId(null);
        await fetchPolicy();
        addToast({ kind: 'success', message: 'MCP server updated' });
      }
    } catch (error) {
      addToast({ kind: 'error', message: getApiErrorMessage(error, 'Failed to save MCP server') });
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkspace = (wsId: number) => {
    setDraft((prev) => ({
      ...prev,
      allowed_workspace_ids: prev.allowed_workspace_ids.includes(wsId)
        ? prev.allowed_workspace_ids.filter((id) => id !== wsId)
        : [...prev.allowed_workspace_ids, wsId],
    }));
  };

  const toggleTool = (tool: string) => {
    setDraft((prev) => {
      const wasSelected = prev.allowed_tool_names.includes(tool);
      return {
        ...prev,
        allowed_tool_names: wasSelected ? prev.allowed_tool_names.filter((t) => t !== tool) : [...prev.allowed_tool_names, tool],
        allowed_applet_api_operations:
          tool === APPLET_API_PARENT_TOOL && wasSelected ? {} : prev.allowed_applet_api_operations,
      };
    });
  };

  const toggleAppletApiOperation = (appletId: string, operationId: string) => {
    setDraft((prev) => {
      const current = prev.allowed_applet_api_operations[appletId] ?? [];
      const next = current.includes(operationId) ? current.filter((id) => id !== operationId) : [...current, operationId];
      const updated: AppletApiAllowlist = {
        ...prev.allowed_applet_api_operations,
      };
      if (next.length > 0) {
        updated[appletId] = next;
      } else {
        delete updated[appletId];
      }
      return {
        ...prev,
        allowed_tool_names: reconcileAppletApiParentTool(prev.allowed_tool_names, updated),
        allowed_applet_api_operations: updated,
      };
    });
  };

  if (loading) {
    return (
      <Page maxWidth="xl">
        <PageBody>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" />
            Loading MCP server settings...
          </div>
        </PageBody>
      </Page>
    );
  }

  if (loadError) {
    return (
      <Page maxWidth="xl">
        <PageHeader title="Outbound Access (MCP)" description="MCP settings could not be loaded." />
        <PageBody>
          <Card><CardContent className="py-6 text-sm text-red-600">{loadError}</CardContent></Card>
        </PageBody>
      </Page>
    );
  }

  const isEditing = editingId !== null;

  return (
    <Page maxWidth="xl">
      <PageHeader
        title="Outbound Access (MCP)"
        description="Manage tenant-wide MCP availability, then configure each applet-specific MCP surface."
        actions={
          <Button variant="outline" size="sm" onClick={() => setAccessHelpOpen(true)} aria-label="Open MCP access explanation">
            <QuestionMarkCircleIcon className="h-4 w-4" />
          </Button>
        }
      />
      <PageBody className="space-y-6">
        <GlobalMcpOverview
          policy={policy}
          workspaceConfig={workspaceConfig}
          workspaceCount={workspaces.length}
          onToggleWorkspace={workspaceConfig ? () => handleToggle(workspaceConfig) : undefined}
          onEditWorkspace={editWorkspaceApplet}
          onOpenAppletApis={() => openApplet('applet_apis')}
          onOpenPrd={() => openApplet('prd_generator')}
          appletApiDescriptors={appletApiDescriptors}
          disabled={isEditing}
        />

        <Tabs value={activeAppletTab} onValueChange={(value) => setActiveAppletTab(value as McpAppletTab)}>
          <div ref={appletSectionRef} />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-title text-xl text-charcoal dark:text-white">Applet MCP servers</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Configure each MCP surface independently after the global tenant gateway is enabled.
              </p>
            </div>
            <TabsList>
              <TabsTrigger value="workspaces" disabled={isEditing && activeAppletTab !== 'workspaces'}>
                Workspaces
              </TabsTrigger>
              <TabsTrigger value="applet_apis" disabled={isEditing && activeAppletTab !== 'applet_apis'}>
                Applet APIs
              </TabsTrigger>
              <TabsTrigger value="prd_generator" disabled={isEditing && activeAppletTab !== 'prd_generator'}>
                PRD Generator
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="workspaces" className="space-y-4">
            {workspaceConfigs.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <ServerStackIcon className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <h3 className="mb-2 text-lg font-medium text-charcoal dark:text-gray-100">Workspace MCP server unavailable</h3>
                  <p className="mx-auto max-w-xl text-gray-500 dark:text-gray-400">
                    The Workspaces MCP applet server is not configured for this tenant. This page only enables, disables, and edits applet
                    servers that already exist.
                  </p>
                </CardContent>
              </Card>
            )}

            {workspaceConfigs.map((config) =>
              editingId === config.id ? (
                <ConfigForm
                  key={config.id}
                  title={`Edit: ${config.display_name || config.server_name}`}
                  draft={draft}
                  setDraft={setDraft}
                  workspaces={workspaces}
                  toggleWorkspace={toggleWorkspace}
                  toggleTool={toggleTool}
                  appletApiDescriptors={appletApiDescriptors}
                  toggleAppletApiOperation={toggleAppletApiOperation}
                  onSave={saveConfig}
                  onCancel={cancelEdit}
                  saving={saving}
                />
              ) : (
                <ConfigCard
                  key={config.id}
                  config={config}
                  workspaceMap={workspaceMap}
                  onToggle={() => handleToggle(config)}
                  onEdit={() => startEdit(config)}
                  onCopy={copyValue}
                  disabled={isEditing}
                  appletApiDescriptors={appletApiDescriptors}
                />
              ),
            )}
          </TabsContent>

          <TabsContent value="applet_apis">
            <AppletApisPanel
              config={workspaceConfig}
              appletApiDescriptors={appletApiDescriptors}
              disabled={isEditing}
              onEdit={editWorkspaceApplet}
            />
          </TabsContent>

          <TabsContent value="prd_generator">
            <PrdMcpAppletPanel policy={policy} onCopy={copyValue} />
          </TabsContent>
        </Tabs>
      </PageBody>
      <Sheet open={accessHelpOpen} onClose={() => setAccessHelpOpen(false)}>
        <SheetContent side="right" size="xl">
          <SheetHeader>
            <SheetTitle>How MCP Access Works</SheetTitle>
            <SheetDescription>
              The checks run in order from the tenant gateway through applet policy, workspace opt-in, and client verification.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <McpAccessMap policy={policy} configs={configs} surface="plain" />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

function McpAccessMap({
  policy,
  configs,
  surface = 'card',
}: {
  policy: MCPTenantPolicy | null;
  configs: MCPConfig[];
  surface?: 'card' | 'plain';
}) {
  const enabledConfigs = configs.filter((config) => config.is_enabled);
  const workspacePolicy = summarizeAllowlist(
    enabledConfigs,
    (config) => config.allowed_workspace_ids,
    'All workspaces',
    'Workspace allowlist',
  );
  const toolPolicy = summarizeAllowlist(enabledConfigs, (config) => config.allowed_tool_names, 'All tools', 'Tool allowlist');
  const appletApiCount = enabledConfigs.reduce(
    (total, config) =>
      total +
      Object.values(config.allowed_applet_api_operations ?? {}).reduce((configTotal, operations) => configTotal + operations.length, 0),
    0,
  );
  const steps = [
    {
      title: 'Tenant gateway',
      description: policy?.tenant_enabled ? 'MCP route and OAuth metadata are enabled.' : 'Enable a tenant MCP policy first.',
      status: policy?.tenant_enabled ? 'ready' : 'blocked',
    },
    {
      title: 'MCP policy',
      description:
        enabledConfigs.length > 0
          ? `${enabledConfigs.length} enabled policy${enabledConfigs.length === 1 ? '' : ' policies'}.`
          : 'Create or enable a policy.',
      status: enabledConfigs.length > 0 ? 'ready' : 'blocked',
    },
    {
      title: 'Allowed workspaces',
      description: workspacePolicy,
      status: enabledConfigs.length > 0 ? 'ready' : 'blocked',
    },
    {
      title: 'Allowed tools',
      description: toolPolicy,
      status: enabledConfigs.length > 0 ? 'ready' : 'blocked',
    },
    {
      title: 'Allowed applet APIs',
      description:
        appletApiCount > 0
          ? `${appletApiCount} applet API operation${appletApiCount === 1 ? '' : 's'} explicitly allowed.`
          : 'Applet API calls are blocked until operations are selected.',
      status: appletApiCount > 0 ? 'ready' : 'manual',
    },
    {
      title: 'Workspace opt-in',
      description: 'Each workspace must turn on external exposure.',
      status: 'manual',
    },
    {
      title: 'Client verification',
      description: 'Test OAuth and live tool calls from the client.',
      status: 'manual',
    },
  ];

  const content = (
    <div className="flex flex-col gap-3">
      {steps.map((step, index) => (
        <React.Fragment key={step.title}>
          <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2/40">
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-charcoal dark:text-white">{step.title}</p>
              <Badge variant={step.status === 'ready' ? 'success' : step.status === 'blocked' ? 'warning' : 'secondary'}>
                {step.status === 'ready' ? 'Ready' : step.status === 'blocked' ? 'Needs action' : 'Manual'}
              </Badge>
            </div>
            <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{step.description}</p>
          </div>
          {index < steps.length - 1 && (
            <div className="flex justify-center">
              <ArrowRightIcon className="h-4 w-4 rotate-90 text-gray-300 dark:text-gray-600" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (surface === 'plain') return content;

  return (
    <Card>
      <CardHeader>
        <CardTitle>How MCP access works</CardTitle>
        <CardDescription>
          Admin settings define the tenant allowlists. Each workspace still chooses whether to expose itself and which live tools pass
          readiness.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
          {steps.map((step, index) => (
            <React.Fragment key={step.title}>
              <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2/40">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-charcoal dark:text-white">{step.title}</p>
                  <Badge variant={step.status === 'ready' ? 'success' : step.status === 'blocked' ? 'warning' : 'secondary'}>
                    {step.status === 'ready' ? 'Ready' : step.status === 'blocked' ? 'Needs action' : 'Manual'}
                  </Badge>
                </div>
                <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden items-center justify-center lg:flex">
                  <ArrowRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function summarizeAllowlist<T>(
  configs: MCPConfig[],
  getValues: (config: MCPConfig) => T[] | null,
  allLabel: string,
  explicitLabel: string,
): string {
  if (configs.length === 0) return 'No enabled policy.';
  if (configs.some((config) => (getValues(config) ?? []).length === 0)) return allLabel;
  const count = configs.reduce((total, config) => total + (getValues(config) ?? []).length, 0);
  return `${explicitLabel}: ${count}`;
}

function GlobalMcpOverview({
  policy,
  workspaceConfig,
  workspaceCount,
  onToggleWorkspace,
  onEditWorkspace,
  onOpenAppletApis,
  onOpenPrd,
  appletApiDescriptors,
  disabled,
}: {
  policy: MCPTenantPolicy | null;
  workspaceConfig: MCPConfig | null;
  workspaceCount: number;
  onToggleWorkspace?: () => void;
  onEditWorkspace: () => void;
  onOpenAppletApis: () => void;
  onOpenPrd: () => void;
  appletApiDescriptors: MCPAppletAPI[];
  disabled: boolean;
}) {
  const isEnabled = Boolean(policy?.tenant_enabled);
  const policyLabel = !policy
    ? 'Loading policy'
    : isEnabled
      ? 'Enabled across applets'
      : policy.policy_state === 'not_configured'
        ? 'Denied by default'
        : 'Disabled across applets';
  const workspaceAllowedCount = workspaceConfig?.allowed_workspace_ids?.length ?? 0;
  const workspaceToolCount = workspaceConfig?.allowed_tool_names?.length ?? 0;
  const workspaceScopeLabel = !workspaceConfig
    ? 'No server configured'
    : workspaceAllowedCount > 0
      ? `${workspaceAllowedCount} workspace${workspaceAllowedCount === 1 ? '' : 's'} exposed`
      : workspaceCount > 0
        ? `${workspaceCount} workspace${workspaceCount === 1 ? '' : 's'} eligible`
        : 'All workspaces eligible';
  const workspaceToolLabel = !workspaceConfig
    ? 'No tools exposed'
    : workspaceToolCount > 0
      ? `${workspaceToolCount} tool${workspaceToolCount === 1 ? '' : 's'} allowed`
      : 'All tools allowed';
  const appletApiOperationCount = Object.values(workspaceConfig?.allowed_applet_api_operations ?? {}).reduce(
    (total, operations) => total + operations.length,
    0,
  );
  const availableAppletApiOperationCount = appletApiDescriptors.reduce((total, descriptor) => total + descriptor.operations.length, 0);
  const appletApiToolAllowed = isToolAllowedByConfig(workspaceConfig, APPLET_API_PARENT_TOOL);
  const appletApiOperationLabel =
    appletApiOperationCount > 0
      ? `${appletApiOperationCount} API operation${appletApiOperationCount === 1 ? '' : 's'} allowed`
      : 'No APIs allowed';
  const appletApiDescriptorLabel =
    availableAppletApiOperationCount > 0
      ? `${availableAppletApiOperationCount} operations available`
      : 'No applet APIs declared';

  return (
    <Card className={!isEnabled ? 'border-amber-200 dark:border-amber-900/40' : undefined}>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Global MCP availability
              <Badge variant={isEnabled ? 'success' : 'warning'}>{policyLabel}</Badge>
            </CardTitle>
            <CardDescription>
              {policy?.policy_reason || 'Loading tenant MCP policy metadata.'} Applet servers below own their connection details and tool
              policy.
            </CardDescription>
          </div>
          <Badge variant="outline" className="h-fit">
            Deny by default
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <McpStatusTile
            label="Tenant gateway"
            value={isEnabled ? 'Enabled' : 'Disabled'}
            status={isEnabled ? 'ready' : 'blocked'}
            description={
              policy?.default_deny
                ? 'Tenant policy denies unapproved applet access by default.'
                : 'Tenant policy is not denying applet access by default.'
            }
          />
          <GlobalAppletSummary
            title="Workspaces"
            enabled={Boolean(workspaceConfig?.is_enabled)}
            primaryDetail={workspaceScopeLabel}
            secondaryDetail={workspaceToolLabel}
            onToggle={onToggleWorkspace}
            onEdit={onEditWorkspace}
            disabled={disabled || !workspaceConfig}
          />
          <GlobalAppletSummary
            title="Applet APIs"
            enabled={Boolean(workspaceConfig?.is_enabled && appletApiToolAllowed && appletApiOperationCount > 0)}
            primaryDetail={appletApiOperationLabel}
            secondaryDetail={appletApiToolAllowed ? appletApiDescriptorLabel : 'Applet APIs tool not allowed'}
            onEdit={onOpenAppletApis}
            disabled={disabled || !workspaceConfig}
            managed
          />
          <GlobalAppletSummary
            title="PRD Generator"
            enabled={isEnabled}
            primaryDetail={`${PRD_MCP_TOOLS.length} tools available`}
            secondaryDetail="Permission-gated applet endpoint"
            onEdit={onOpenPrd}
            disabled={disabled}
            managed
          />
        </div>
      </CardContent>
    </Card>
  );
}

function McpStatusTile({
  label,
  value,
  status,
  description,
}: {
  label: string;
  value: string;
  status: 'ready' | 'blocked' | 'manual';
  description?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/40">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-charcoal dark:text-white">{value}</p>
        <Badge variant={status === 'ready' ? 'success' : status === 'blocked' ? 'warning' : 'secondary'}>
          {status === 'ready' ? 'Ready' : status === 'blocked' ? 'Action' : 'Policy'}
        </Badge>
      </div>
      {description ? <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</p> : null}
    </div>
  );
}

function GlobalAppletSummary({
  title,
  enabled,
  primaryDetail,
  secondaryDetail,
  onToggle,
  onEdit,
  disabled,
  managed = false,
}: {
  title: string;
  enabled: boolean;
  primaryDetail: string;
  secondaryDetail: string;
  onToggle?: () => void;
  onEdit: () => void;
  disabled: boolean;
  managed?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/40">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{title} server</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-charcoal dark:text-white">{enabled ? 'Enabled' : 'Disabled'}</p>
            <Badge variant={enabled ? 'success' : 'warning'}>{managed ? 'Managed' : enabled ? 'Ready' : 'Off'}</Badge>
          </div>
        </div>
        {onToggle ? <Switch checked={enabled} onCheckedChange={onToggle} disabled={disabled} /> : null}
      </div>
      <div className="space-y-1">
        <p className="text-sm text-charcoal dark:text-gray-100">{primaryDetail}</p>
        <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{secondaryDetail}</p>
      </div>
      <Button
        className="mt-4"
        variant="outline"
        size="sm"
        onClick={onEdit}
        disabled={disabled}
        aria-label={`Edit ${title} applet MCP server`}
      >
        <PencilIcon className="mr-2 h-4 w-4" />
        Edit
      </Button>
    </div>
  );
}

function PrdMcpAppletPanel({
  policy,
  onCopy,
}: {
  policy: MCPTenantPolicy | null;
  onCopy: (label: string, value: string) => Promise<void>;
}) {
  const isEnabled = Boolean(policy?.tenant_enabled);
  const details = getPrdMcpConnectionDetails(policy);

  return (
    <Card className={!isEnabled ? 'border-amber-200 dark:border-amber-900/40' : undefined}>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-eliza-red/10">
              <ServerStackIcon className="h-5 w-5 text-eliza-red" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                PRD Generator MCP Server
                <Badge variant={isEnabled ? 'success' : 'warning'}>{isEnabled ? 'Available' : 'Blocked'}</Badge>
              </CardTitle>
              <CardDescription>
                Dedicated applet endpoint for generating PRDs. It is governed by PRD Generator permissions, not workspace allowlists.
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="h-fit">
            Managed applet server
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <McpStatusTile label="Endpoint" value="/mcp/prd" status={isEnabled ? 'ready' : 'blocked'} />
          <McpStatusTile label="Required scope" value="prd_generator:write" status={isEnabled ? 'ready' : 'blocked'} />
          <McpStatusTile label="Tools" value={`${PRD_MCP_TOOLS.length} tools`} status={isEnabled ? 'ready' : 'blocked'} />
          <McpStatusTile label="Policy model" value="Permission gated" status="manual" />
        </div>

        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Available Tools</p>
          <div className="flex flex-wrap gap-2">
            {PRD_MCP_TOOLS.map((tool) => (
              <Badge key={tool} variant="outline" className="text-xs">
                {tool}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <ConnectionDetailRow label="MCP Server URL" value={details.serverUrl} onCopy={onCopy} />
          <ConnectionDetailRow label="Protected Resource Metadata" value={details.protectedResourceMetadataUrl} onCopy={onCopy} />
          <ConnectionDetailRow label="Authorization Metadata" value={details.authorizationMetadataUrl} onCopy={onCopy} />
          <ConnectionDetailRow label="Authorization URL" value={details.authorizationUrl} onCopy={onCopy} />
          <ConnectionDetailRow label="Token URL" value={details.tokenUrl} onCopy={onCopy} />
        </div>
      </CardContent>
    </Card>
  );
}

function AppletApisPanel({
  config,
  appletApiDescriptors,
  disabled,
  onEdit,
}: {
  config: MCPConfig | null;
  appletApiDescriptors: MCPAppletAPI[];
  disabled: boolean;
  onEdit: () => void;
}) {
  const selectedOperationCount = Object.values(config?.allowed_applet_api_operations ?? {}).reduce(
    (total, operations) => total + operations.length,
    0,
  );
  const availableOperationCount = appletApiDescriptors.reduce((total, descriptor) => total + descriptor.operations.length, 0);
  const appletApiToolAllowed = isToolAllowedByConfig(config, APPLET_API_PARENT_TOOL);
  const isAvailable = Boolean(config?.is_enabled && appletApiToolAllowed && selectedOperationCount > 0);

  return (
    <Card className={!isAvailable ? 'border-amber-200 dark:border-amber-900/40' : undefined}>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-eliza-red/10">
              <ServerStackIcon className="h-5 w-5 text-eliza-red" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                Applet APIs
                <Badge variant={isAvailable ? 'success' : 'warning'}>{isAvailable ? 'Allowed' : 'Default off'}</Badge>
              </CardTitle>
              <CardDescription>
                Enabled applets can expose manifest-declared API operations through the tenant MCP server when explicitly selected.
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onEdit} disabled={disabled || !config}>
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit allowlist
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <McpStatusTile
            label="MCP tool"
            value="Applet APIs"
            status={config?.is_enabled && appletApiToolAllowed ? 'ready' : 'blocked'}
          />
          <McpStatusTile
            label="Concrete tools"
            value={`${selectedOperationCount} allowed`}
            status={selectedOperationCount > 0 ? 'ready' : 'blocked'}
          />
          <McpStatusTile
            label="Declared APIs"
            value={`${availableOperationCount} available`}
            status={availableOperationCount > 0 ? 'ready' : 'blocked'}
          />
          <McpStatusTile label="Policy model" value="Explicit allowlist" status="manual" />
        </div>

        {appletApiDescriptors.length > 0 ? (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Available API Operations</p>
            <div className="space-y-3">
              {appletApiDescriptors.map((descriptor) => {
                const selectedAppletOperations = config?.allowed_applet_api_operations?.[descriptor.applet_id] ?? [];
                return (
                  <div
                    key={descriptor.applet_id}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface"
                  >
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-charcoal dark:text-white">{descriptor.display_name}</p>
                        {descriptor.description ? (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{descriptor.description}</p>
                        ) : null}
                      </div>
                      <Badge variant={selectedAppletOperations.length > 0 ? 'success' : 'secondary'}>
                        {selectedAppletOperations.length > 0 ? `${selectedAppletOperations.length} allowed` : 'Off'}
                      </Badge>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                      {descriptor.operations.map((operation) => {
                        const selected = selectedAppletOperations.includes(operation.operation_id);
                        return (
                          <div
                            key={operation.operation_id}
                            className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface-2/50"
                          >
                            <div className="mb-2 flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-charcoal dark:text-white">{operation.title}</p>
                                <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                  {operation.method} {operation.path}
                                </p>
                                <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">{operation.tool_name}</p>
                              </div>
                              <Badge variant={selected ? 'success' : 'secondary'}>{selected ? 'Allowed' : 'Off'}</Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant={operation.read_only ? 'secondary' : 'warning'} className="text-xs">
                                {operation.read_only ? 'Read-only' : 'Write'}
                              </Badge>
                            </div>
                            {operation.description ? (
                              <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{operation.description}</p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-sm text-amber-600 dark:text-amber-400">No enabled applets declare MCP API operations.</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================
   Config Card (read-only view)
   ============================ */

function ConfigCard({
  config,
  workspaceMap,
  onToggle,
  onEdit,
  onCopy,
  disabled,
  appletApiDescriptors,
}: {
  config: MCPConfig;
  workspaceMap: Map<number, string>;
  onToggle: () => void;
  onEdit: () => void;
  onCopy: (label: string, value: string) => Promise<void>;
  disabled: boolean;
  appletApiDescriptors: MCPAppletAPI[];
}) {
  const allowedWs = config.allowed_workspace_ids ?? [];
  const allowedTools = config.allowed_tool_names ?? [];
  const allowedAppletApiCount = Object.values(config.allowed_applet_api_operations ?? {}).reduce(
    (total, operations) => total + operations.length,
    0,
  );
  const knownAppletApiCount = appletApiDescriptors.reduce((total, descriptor) => total + descriptor.operations.length, 0);
  const redirectUris = config.oauth_redirect_uris ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-eliza-red/10">
              <ServerStackIcon className="h-5 w-5 text-eliza-red" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                {config.display_name || config.server_name}
                <Badge variant={config.is_enabled ? 'success' : 'default'}>{config.is_enabled ? 'Enabled' : 'Disabled'}</Badge>
              </CardTitle>
              <CardDescription>{config.description || `Type: ${config.server_type} · Name: ${config.server_name}`}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={config.is_enabled} onCheckedChange={onToggle} disabled={disabled} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={disabled}
              aria-label={`Edit ${config.display_name || config.server_name}`}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {/* Workspaces */}
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Workspaces</p>
            {allowedWs.length === 0 ? (
              <span className="text-sm text-gray-600 dark:text-gray-300">All workspaces</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {allowedWs.map((id) => (
                  <Badge key={id} variant="outline" className="text-xs">
                    {workspaceMap.get(id) || `#${id}`}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Tools */}
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tools</p>
            {allowedTools.length === 0 ? (
              <span className="text-sm text-gray-600 dark:text-gray-300">All tools</span>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-300">{allowedTools.length} tools</span>
            )}
          </div>

          {/* Applet APIs */}
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Applet APIs</p>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {allowedAppletApiCount > 0 ? `${allowedAppletApiCount} allowed` : knownAppletApiCount > 0 ? 'None allowed' : 'Unavailable'}
            </span>
          </div>
          {/* Rate Limits */}
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rate Limits</p>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {config.rate_limit_rpm} rpm · {config.rate_limit_rph} rph
            </span>
          </div>

          {/* Sessions */}
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Concurrency</p>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {config.max_concurrent_sessions} sessions · {config.max_async_operations} async ops
            </span>
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4 dark:border-dark-border">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-charcoal dark:text-gray-100">Connection Details</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Use these values when adding the connector in ChatGPT or Claude.</p>
            </div>
            <Badge variant="outline" className="text-xs">
              No client secret required
            </Badge>
          </div>

          <div className="space-y-3">
            <ConnectionDetailRow label="MCP Server URL" value={config.server_url} onCopy={onCopy} />
            <ConnectionDetailRow label="Authorization URL" value={config.authorization_url} onCopy={onCopy} />
            <ConnectionDetailRow label="Token URL" value={config.token_url} onCopy={onCopy} />
            {config.oauth_client_id ? <ConnectionDetailRow label="OAuth Client ID" value={config.oauth_client_id} onCopy={onCopy} /> : null}
            {redirectUris.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Registered Redirect URIs</p>
                <div className="space-y-2">
                  {redirectUris.map((uri) => (
                    <ConnectionDetailRow key={uri} label="Redirect URI" value={uri} onCopy={onCopy} />
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Add the callback URL from ChatGPT or Claude to the redirect URI list before testing OAuth.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectionDetailRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (label: string, value: string) => Promise<void>;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
        <Button variant="ghost" size="sm" onClick={() => void onCopy(label, value)}>
          <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
          Copy
        </Button>
      </div>
      <code className="block overflow-x-auto whitespace-nowrap text-xs text-charcoal dark:text-gray-100">{value}</code>
    </div>
  );
}

/* ============================
   Config Form (create / edit)
   ============================ */

function ConfigForm({
  title,
  draft,
  setDraft,
  workspaces,
  toggleWorkspace,
  toggleTool,
  appletApiDescriptors,
  toggleAppletApiOperation,
  onSave,
  onCancel,
  saving,
}: {
  title: string;
  draft: {
    display_name: string;
    description: string;
    is_enabled: boolean;
    allowed_workspace_ids: number[];
    allowed_tool_names: string[];
    allowed_applet_api_operations: AppletApiAllowlist;
    rate_limit_rpm: number;
    rate_limit_rph: number;
    oauth_client_id: string;
    oauth_redirect_uris_text: string;
  };
  setDraft: React.Dispatch<React.SetStateAction<typeof draft>>;
  workspaces: Workspace[];
  toggleWorkspace: (id: number) => void;
  toggleTool: (tool: string) => void;
  appletApiDescriptors: MCPAppletAPI[];
  toggleAppletApiOperation: (appletId: string, operationId: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <Card className="border-eliza-red/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ServerStackIcon className="h-5 w-5 text-eliza-red" />
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
              <XMarkIcon className="mr-1 h-4 w-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="mr-2" /> : <CheckIcon className="mr-1 h-4 w-4" />}
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input
              value={draft.display_name}
              onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
              placeholder="Workspaces MCP Server"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="MCP access to workspace knowledge bases"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>OAuth Client ID</Label>
            <Input
              value={draft.oauth_client_id}
              onChange={(e) => setDraft((d) => ({ ...d, oauth_client_id: e.target.value }))}
              placeholder="Optional. Leave blank to auto-generate on create"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Shown in the connection details so admins can paste it into ChatGPT or Claude if needed.
            </p>
          </div>

          <div className="space-y-2">
            <Label>OAuth Redirect URIs</Label>
            <Textarea
              rows={4}
              value={draft.oauth_redirect_uris_text}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  oauth_redirect_uris_text: e.target.value,
                }))
              }
              placeholder={
                'Paste one callback URL per line\nhttps://chatgpt.com/connector/oauth/callback\nhttps://claude.ai/api/mcp/auth_callback'
              }
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Paste the callback URL supplied by ChatGPT or Claude. Client secrets are not used yet.
            </p>
          </div>
        </div>

        {/* Enabled */}
        <div className="flex items-center gap-3">
          <Switch checked={draft.is_enabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, is_enabled: v }))} />
          <Label className="cursor-pointer">Enabled</Label>
        </div>

        {/* Workspace Selection */}
        <div>
          <Label className="mb-2 block">Allowed Workspaces</Label>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Select which workspaces are accessible via MCP. Leave empty to allow all.
          </p>
          {workspaces.length === 0 ? (
            <p className="text-sm text-gray-400">No workspaces found</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {workspaces.map((ws) => {
                const selected = draft.allowed_workspace_ids.includes(ws.id);
                return (
                  <button
                    key={ws.id}
                    type="button"
                    onClick={() => toggleWorkspace(ws.id)}
                    className={`
                      flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors
                      ${
                        selected
                          ? 'border-eliza-red/40 bg-eliza-red/5 text-charcoal dark:text-white'
                          : 'border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                      }
                    `}
                  >
                    <div
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                        selected ? 'border-eliza-red bg-eliza-red text-white' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {selected && <CheckIcon className="h-3 w-3" />}
                    </div>
                    <span className="truncate">{ws.name}</span>
                  </button>
                );
              })}
            </div>
          )}
          {draft.allowed_workspace_ids.length === 0 && workspaces.length > 0 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">No workspaces selected — all workspaces will be accessible.</p>
          )}
        </div>

        {/* Tool Selection */}
        <div>
          <Label className="mb-2 block">Allowed Tools</Label>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Select which MCP tools are available. Leave empty to allow all tools.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {KNOWN_TOOLS.map((tool) => {
              const selected = draft.allowed_tool_names.includes(tool);
              return (
                <button
                  key={tool}
                  type="button"
                  onClick={() => toggleTool(tool)}
                  className={`
                    flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors
                    ${
                      selected
                        ? 'border-eliza-red/40 bg-eliza-red/5 text-charcoal dark:text-white'
                        : 'border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                    }
                  `}
                >
                  <div
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                      selected ? 'border-eliza-red bg-eliza-red text-white' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {selected && <CheckIcon className="h-3 w-3" />}
                  </div>
                  <span className={tool === APPLET_API_PARENT_TOOL ? 'text-sm font-medium' : 'font-mono text-xs'}>
                    {toolDisplayLabel(tool)}
                  </span>
                </button>
              );
            })}
          </div>
          {draft.allowed_tool_names.length === 0 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">No tools selected — all tools will be available.</p>
          )}
        </div>

        {/* Applet API Selection */}
        {appletApiDescriptors.length > 0 ? (
          <div>
            <div className="mb-3">
              <Label className="mb-2 block">Applet APIs</Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enable an applet, then select the concrete API tools exposed through MCP.
              </p>
            </div>
            <div className="space-y-3">
              {appletApiDescriptors.map((descriptor) => {
                const selectedAppletOperations = draft.allowed_applet_api_operations[descriptor.applet_id] ?? [];
                const appletEnabled = selectedAppletOperations.length > 0;
                return (
                  <details
                    key={descriptor.applet_id}
                    open={appletEnabled}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-charcoal dark:text-white">{descriptor.display_name}</p>
                        {descriptor.description ? (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{descriptor.description}</p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={appletEnabled ? 'success' : 'warning'} className="h-fit">
                          {appletEnabled ? `${selectedAppletOperations.length} selected` : 'Default off'}
                        </Badge>
                      </div>
                    </summary>
                    <div className="mt-3 grid gap-2 lg:grid-cols-2">
                      {descriptor.operations.map((operation) => {
                        const selected = selectedAppletOperations.includes(operation.operation_id);
                        return (
                          <button
                            key={operation.operation_id}
                            type="button"
                            onClick={() => toggleAppletApiOperation(descriptor.applet_id, operation.operation_id)}
                            className={`
                              flex items-start gap-3 rounded-lg border px-3 py-3 text-left text-sm transition-colors
                              ${
                                selected
                                  ? 'border-eliza-red/40 bg-eliza-red/5 text-charcoal dark:text-white'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-dark-border dark:text-gray-400 dark:hover:border-gray-500'
                              }
                            `}
                          >
                            <div
                              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                                selected ? 'border-eliza-red bg-eliza-red text-white' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            >
                              {selected && <CheckIcon className="h-3 w-3" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-charcoal dark:text-white">{operation.title}</span>
                                <Badge variant="outline" className="text-xs">
                                  {operation.method}
                                </Badge>
                              </div>
                              <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">{operation.tool_name}</p>
                              <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">{operation.path}</p>
                              {operation.description ? (
                                <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{operation.description}</p>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </details>
                );
              })}
            </div>
            {!hasSelectedAppletApiOperations(draft.allowed_applet_api_operations) ? (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                No applet APIs selected — concrete applet API tools will be blocked.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                The Applet APIs parent tool is automatically included for restricted tool allowlists.
              </p>
            )}
          </div>
        ) : null}

        {/* Rate Limits */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Requests per minute</Label>
            <Input
              type="number"
              min={1}
              max={10000}
              value={draft.rate_limit_rpm}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  rate_limit_rpm: Number(e.target.value) || 30,
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Requests per hour</Label>
            <Input
              type="number"
              min={1}
              max={100000}
              value={draft.rate_limit_rph}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  rate_limit_rph: Number(e.target.value) || 500,
                }))
              }
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
